import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import type { AcceptInvitationBody, InvitationPreview, Me } from "@gioia365/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useRouteContext } from "@tanstack/react-router";
import { authApi, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, type AuthFailure } from "../api/auth";
import { ApiRequestError } from "../api/http";
import { acceptInvitation, meQuery, myMembershipsQuery, previewInvitation } from "../api/queries";
import { Button } from "../design/components/Button";
import { Card } from "../design/components/Card";
import { missingNamePart, NameFields, type NamePart } from "../design/components/NameFields";
import { PublicShell } from "../design/components/PublicShell";
import { TextField } from "../design/components/TextField";
import "../design/components/button.css";
import { messageForError } from "../i18n/errors";
import { deviceTimeZone, formatNumericDate } from "../i18n/format";
import { useT, type Translate } from "../i18n/locale";
import { clearTabContext, writeTabContext } from "../session/tab-context";
import { describeAuthFailure } from "./auth-failure";
import "./auth.css";

// La pagina dell'invito (`/invito#<token>`, piano M1b Task 6): pubblica, fuori dalle guardie. Il token è nel
// frammento del link, che il browser non manda al server: si legge una volta, l'indirizzo si ripulisce, e da
// lì il token viaggia soltanto nel corpo delle richieste (anteprima e accettazione).
//
// Quattro casi, secondo la sessione e l'identità dell'invitato:
//   1. nessuna sessione, identità nuova → nome e cognome (già quelli dell'invito) e password nuova;
//   2. nessuna sessione, identità esistente → la sua password;
//   3. sessione aperta → un clic. L'email dell'invito è mascherata, quindi la pagina non può sapere se la
//      sessione è della stessa persona: prova l'accettazione e il server, che confronta le email, risponde;
//   4. `403 session_mismatch` → la sessione è di un'altra persona: «Esci» e si riparte dallo stesso invito.
// Se la sessione letta all'apertura non c'è più quando si accetta (cookie scaduto, uscita da un'altra scheda), il
// server chiede la password: la pagina rilegge la sessione e passa al modulo del caso 1 o 2, con lo stesso token.

/** Il token del link (`/invito#<token>`), decodificato; `null` se manca o non si legge. */
function readTokenFromHash(hash: string): string | null {
  if (hash.length <= 1) return null;
  try {
    return decodeURIComponent(hash.slice(1)) || null;
  } catch {
    return null;
  }
}

/** I rifiuti del server che la pagina spiega da sé, senza modulo. */
const REFUSALS = ["invitation_invalid", "invitation_expired", "invitation_revoked", "invitation_used", "already_member", "organization_unavailable"] as const;
type Refusal = (typeof REFUSALS)[number];

function refusalOf(error: unknown): Refusal | null {
  return error instanceof ApiRequestError ? (REFUSALS.find((code) => code === error.code) ?? null) : null;
}

/**
 * Il rifiuto dell'anteprima: come `refusalOf`, più un token che il server non riconosce nemmeno come forma
 * (422) o una rotta che non c'è (404), che valgono «questo link non vale più». All'accettazione un 422 parla
 * invece di un campo del modulo.
 */
function previewRefusalOf(error: unknown): Refusal | null {
  const refusal = refusalOf(error);
  if (refusal) return refusal;
  return error instanceof ApiRequestError && (error.code === "validation_failed" || error.code === "not_found") ? "invitation_invalid" : null;
}

/**
 * Il testo di un rifiuto. La data di scadenza e l'azienda le conosce soltanto chi ha già letto l'anteprima:
 * se l'anteprima stessa è rifiutata, il server non le dice, e il testo è quello senza data. La data è in numeri,
 * nel fuso del dispositivo: l'anteprima non porta il fuso dell'azienda che invita.
 */
function describeRefusal(t: Translate, refusal: Refusal, preview: InvitationPreview | undefined): { message: string; offersLogin: boolean } {
  switch (refusal) {
    case "invitation_invalid":
      return { message: t("errors.invitation_invalid"), offersLogin: false };
    case "invitation_expired":
      return {
        message: preview
          ? t("invitation.expiredOn", { date: formatNumericDate(deviceTimeZone(), preview.expiresAt) })
          : t("errors.invitation_expired"),
        offersLogin: false,
      };
    case "invitation_revoked":
      return { message: t("errors.invitation_revoked"), offersLogin: false };
    case "invitation_used":
      return { message: t("errors.invitation_used"), offersLogin: true };
    case "already_member":
      return {
        message: preview ? t("invitation.alreadyMember", { organization: preview.organizationName }) : t("errors.already_member"),
        offersLogin: true,
      };
    case "organization_unavailable":
      return {
        message: preview ? t("invitation.organizationInactive", { organization: preview.organizationName }) : t("errors.organization_unavailable"),
        offersLogin: false,
      };
  }
}

export function InvitationPage() {
  const t = useT();
  // Il token si legge una volta sola e poi sparisce dalla barra degli indirizzi (effetto qui sotto): resta
  // soltanto nello stato del componente, non nella cronologia, nei preferiti o in uno screenshot.
  const [token] = useState(() => readTokenFromHash(window.location.hash));

  useEffect(() => {
    if (window.location.hash === "") return;
    const { pathname, search } = window.location;
    // Si conserva `history.state`, dove il router tiene la propria posizione nella cronologia.
    window.history.replaceState(window.history.state, "", `${pathname}${search}`);
  }, []);

  // L'invito è pubblico e non sa di chi sia la sessione aperta (`session_mismatch` lo scopre all'accettazione):
  // il selettore della lingua non deve mai salvare nulla sull'account di un'altra persona.
  return (
    <PublicShell personLanguage={false}>
      <Card className="g-stack">
        {token ? (
          <InvitationFlow token={token} />
        ) : (
          <>
            <h1>{t("invitation.titleGeneric")}</h1>
            <Result message={t("invitation.incompleteLink")} />
          </>
        )}
      </Card>
    </PublicShell>
  );
}

/** Un esito al posto del modulo: prende il focus quando compare, così viene letto. */
function Result({ message, children }: { message: string; children?: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  return (
    <div ref={ref} className="g-stack g-auth__result" tabIndex={-1} role="status">
      <p className="g-auth__message" data-tone="error">{message}</p>
      {children ? <div className="g-auth__actions">{children}</div> : null}
    </div>
  );
}

function LoginLink() {
  const t = useT();
  return (
    <Link to="/login" className="g-button" data-variant="secondary">
      {t("invitation.goToLogin")}
    </Link>
  );
}

/** Anteprima dell'invito e sessione, poi il modulo giusto per il caso. */
function InvitationFlow({ token }: { token: string }) {
  const t = useT();
  const preview = useQuery({
    // Senza il token nella chiave: la cache non deve ricordarlo. Vale soltanto finché la pagina è aperta.
    queryKey: ["invito", "anteprima"],
    queryFn: ({ signal }) => previewInvitation(token, signal),
    staleTime: Infinity,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });
  const me = useQuery(meQuery);

  if (preview.data === undefined && preview.isError && !preview.isFetching) {
    const refusal = previewRefusalOf(preview.error);
    const described = refusal ? describeRefusal(t, refusal, undefined) : null;
    return (
      <>
        <h1>{t("invitation.titleGeneric")}</h1>
        {described ? (
          <Result message={described.message}>{described.offersLogin ? <LoginLink /> : null}</Result>
        ) : (
          <Result message={messageForError(t, preview.error)}>
            <Button onClick={() => void preview.refetch()}>{t("common.retry")}</Button>
          </Result>
        )}
      </>
    );
  }
  // Una sessione che non si riesce a leggere (server guasto) vale «nessuna sessione»: se ce n'è una, il
  // server lo dice all'accettazione (`session_mismatch`) e la pagina lo spiega.
  if (preview.data === undefined || me.isPending) {
    return (
      <>
        <h1>{t("invitation.titleGeneric")}</h1>
        <p className="g-auth__muted" role="status" aria-busy="true">{t("common.loading")}</p>
      </>
    );
  }
  return <InvitationForm token={token} preview={preview.data} session={me.data ?? null} />;
}

type Problem =
  /** Nome e cognome hanno un solo errore, letto sulla casella che manca (o su quelle che il server rifiuta). */
  | { field: "name"; part: NamePart | "both" }
  | { field: "password"; reason: "missing" | "too_short" | "too_long" | "wrong" }
  | { field: "confirm"; reason: "mismatch" }
  | { field: "form"; error: unknown };

function fieldsOf(error: ApiRequestError): string[] {
  const fields = error.details?.fields;
  return Array.isArray(fields) ? fields.filter((field): field is string => typeof field === "string") : [];
}

function InvitationForm({ token, preview, session }: { token: string; preview: InvitationPreview; session: Me | null }) {
  const t = useT();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const tabStorage = useRouteContext({ from: "__root__", select: (context) => context.tabStorage });
  const mode = session ? "session" : preview.identityExists ? "existing" : "new";

  const [firstName, setFirstName] = useState(preview.firstName);
  const [lastName, setLastName] = useState(preview.lastName);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  // Gli errori dei campi sono codici e non testi, così seguono il cambio di lingua.
  const [problem, setProblem] = useState<Problem | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [otherPerson, setOtherPerson] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [signOutFailure, setSignOutFailure] = useState<AuthFailure | null>(null);
  // Il secondo invio prima che React ridisegni il pulsante si ferma qui, non nello stato.
  const inFlight = useRef(false);
  // «Esci», o una sessione che non c'è più, chiede di portare il focus sul modulo appena comparso: si fa quando
  // la pagina si è ridisegnata.
  const focusFormWithoutSession = useRef(false);
  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmRef = useRef<HTMLInputElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);

  // Il focus va dove si può correggere; per un errore del modulo intero, sul pulsante che si riattiva.
  useEffect(() => {
    if (submitting || !problem) return;
    if (problem.field === "name") (problem.part === "lastName" ? lastNameRef : firstNameRef).current?.focus();
    else if (problem.field === "password") passwordRef.current?.focus();
    else if (problem.field === "confirm") confirmRef.current?.focus();
    else submitRef.current?.focus();
  }, [problem, submitting]);

  // Dopo «Esci», o quando la sessione non c'è più, il modulo di chi non ha una sessione prende il posto del
  // messaggio o del clic: il focus va alla password.
  useEffect(() => {
    if (!focusFormWithoutSession.current || otherPerson || session !== null) return;
    (passwordRef.current ?? submitRef.current)?.focus();
    focusFormWithoutSession.current = false;
  }, [otherPerson, session]);

  function reject(next: Problem) {
    setProblem(next);
  }

  /** Il modulo che l'accettazione non ha accettato: il motivo, sul campo o in una riga sopra il pulsante. */
  function showFailure(error: unknown) {
    const refused = refusalOf(error);
    if (refused) {
      setRefusal(refused);
      return;
    }
    if (error instanceof ApiRequestError) {
      if (error.code === "session_mismatch") {
        setOtherPerson(true);
        return;
      }
      if (error.code === "invalid_credentials") {
        reject({ field: "password", reason: "wrong" });
        return;
      }
      if (error.code === "validation_failed") {
        const fields = fieldsOf(error);
        if (fields.includes("password")) {
          reject({ field: "password", reason: mode === "new" ? "too_short" : "wrong" });
          return;
        }
        const first = fields.includes("firstName");
        const last = fields.includes("lastName");
        if (first || last) {
          reject({ field: "name", part: first && last ? "both" : first ? "firstName" : "lastName" });
          return;
        }
      }
    }
    reject({ field: "form", error });
  }

  /**
   * La sessione di prima non c'è più (uscita o scaduta): la pagina, che tiene il token in memoria, non conserva
   * nulla di quella persona, né l'azienda della scheda né i dati letti con la sua sessione, dell'azienda o della
   * piattaforma. Resta soltanto l'anteprima dell'invito. `["me"]` si svuota senza toglierlo: la pagina lo osserva.
   */
  function forgetPreviousSession() {
    clearTabContext(tabStorage);
    for (const queryKey of [["scheda"], myMembershipsQuery.queryKey, ["piattaforma"]]) queryClient.removeQueries({ queryKey });
    queryClient.setQueryData(meQuery.queryKey, null);
  }

  /**
   * Con una sessione in memoria il server chiede la password (`validation_failed` su `password`) o risponde `401`
   * soltanto se la sessione non c'è più. Si rilegge: senza sessione si dimentica quella di prima
   * (`forgetPreviousSession`) e compare il modulo con la password (identità nuova o esistente, secondo
   * l'anteprima), con il token in memoria. Se la sessione c'è ancora il rifiuto si mostra sul modulo, non su un
   * campo nascosto.
   */
  async function recoverFromLostSession(error: unknown): Promise<boolean> {
    const lost =
      mode === "session" &&
      error instanceof ApiRequestError &&
      (error.status === 401 || (error.code === "validation_failed" && fieldsOf(error).includes("password")));
    if (!lost) return false;
    focusFormWithoutSession.current = true;
    await queryClient.invalidateQueries({ queryKey: meQuery.queryKey });
    if (queryClient.getQueryData(meQuery.queryKey) === null) {
      forgetPreviousSession();
      return true;
    }
    focusFormWithoutSession.current = false;
    reject({ field: "form", error });
    return true;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (inFlight.current) return;
    let body: AcceptInvitationBody;
    if (mode === "session") {
      // La sessione basta: nessuna password. Il server confronta l'email della sessione con quella dell'invito.
      body = { token };
    } else if (mode === "existing") {
      if (password === "") return reject({ field: "password", reason: "missing" });
      // La password di un'identità esistente non cambia: qui serve soltanto a provare che è la sua.
      body = { token, password };
    } else {
      const first = firstName.trim();
      const last = lastName.trim();
      const missing = missingNamePart(first, last);
      if (missing) return reject({ field: "name", part: missing });
      if (password.length < PASSWORD_MIN_LENGTH) return reject({ field: "password", reason: "too_short" });
      if (password.length > PASSWORD_MAX_LENGTH) return reject({ field: "password", reason: "too_long" });
      if (password !== confirm) return reject({ field: "confirm", reason: "mismatch" });
      // Sempre tutti e due, anche se sono ancora quelli dell'invito: il server li vuole insieme.
      body = { token, password, firstName: first, lastName: last };
    }
    inFlight.current = true;
    setSubmitting(true);
    setProblem(null);
    try {
      const accepted = await acceptInvitation(body);
      // Il server può aver aperto la sessione con l'accettazione: ciò che la cache ricordava dell'identità
      // («nessuna sessione») non vale più, e la guardia della Home la rilegge. La scheda lavora nell'azienda
      // appena entrata, al posto di quella che ricordava.
      writeTabContext(tabStorage, { kind: "membership", membershipId: accepted.membershipId });
      queryClient.removeQueries({ queryKey: meQuery.queryKey });
      // Il pulsante resta disabilitato: da qui in poi la pagina si lascia.
      await navigate({ to: "/" });
    } catch (error) {
      // Il pulsante resta disabilitato finché la sessione non è riletta.
      const recovered = await recoverFromLostSession(error);
      inFlight.current = false;
      setSubmitting(false);
      if (!recovered) showFailure(error);
    }
  }

  async function signOut() {
    setSigningOut(true);
    setSignOutFailure(null);
    const result = await authApi.signOut();
    if (!result.ok) {
      setSigningOut(false);
      setSignOutFailure(result.failure);
      return;
    }
    // Si esce senza lasciare la pagina: dell'altra persona non resta nulla.
    forgetPreviousSession();
    focusFormWithoutSession.current = true;
    setOtherPerson(false);
    setSigningOut(false);
  }

  const title = <h1 id="invitation-title">{t("invitation.title", { organization: preview.organizationName })}</h1>;

  if (refusal) {
    const described = describeRefusal(t, refusal, preview);
    return (
      <>
        {title}
        <Result message={described.message}>{described.offersLogin ? <LoginLink /> : null}</Result>
      </>
    );
  }

  const summary = (
    <p className="g-auth__muted">
      {t("invitation.summary", {
        email: preview.email,
        role: preview.roleName,
        date: formatNumericDate(deviceTimeZone(), preview.expiresAt),
      })}
    </p>
  );

  if (otherPerson) {
    return (
      <>
        {title}
        {summary}
        <Result message={t("errors.session_mismatch")}>
          <Button variant="primary" onClick={() => void signOut()} disabled={signingOut}>
            {signingOut ? t("common.signingOut") : t("common.signOut")}
          </Button>
        </Result>
        {signOutFailure ? (
          <p className="g-auth__message" data-tone="error" role="alert">
            {describeAuthFailure(t, signOutFailure)}
          </p>
        ) : null}
      </>
    );
  }

  function passwordError(): string | undefined {
    if (problem?.field !== "password") return undefined;
    switch (problem.reason) {
      case "missing":
        return t("invitation.passwordMissing");
      case "wrong":
        return t("invitation.wrongPassword");
      case "too_short":
        return describeAuthFailure(t, "password_too_short");
      case "too_long":
        return describeAuthFailure(t, "password_too_long");
    }
  }

  return (
    <>
      {title}
      {summary}
      <form className="g-stack" onSubmit={(event) => void handleSubmit(event)} aria-labelledby="invitation-title" aria-busy={submitting} noValidate>
        {mode === "existing" ? <p className="g-auth__muted">{t("invitation.existingIntro")}</p> : null}
        {mode === "session" ? <p className="g-auth__muted">{t("invitation.signedInAs", { email: session?.email ?? "" })}</p> : null}
        {mode === "new" ? (
          <NameFields
            id="invitation-name"
            firstName={{ label: t("common.firstName"), value: firstName, onChange: setFirstName, ref: firstNameRef }}
            lastName={{ label: t("common.lastName"), value: lastName, onChange: setLastName, ref: lastNameRef }}
            autoComplete="person"
            required
            error={problem?.field === "name" ? t("common.nameMissing") : undefined}
            invalid={problem?.field === "name" ? problem.part : undefined}
          />
        ) : null}
        {mode !== "session" ? (
          <TextField
            id="invitation-password"
            ref={passwordRef}
            label={mode === "new" ? t("invitation.newPassword", { min: PASSWORD_MIN_LENGTH }) : t("auth.login.password")}
            type="password"
            autoComplete={mode === "new" ? "new-password" : "current-password"}
            required
            value={password}
            error={passwordError()}
            onChange={(event) => setPassword(event.target.value)}
          />
        ) : null}
        {mode === "new" ? (
          <TextField
            id="invitation-confirm"
            ref={confirmRef}
            label={t("invitation.confirmPassword")}
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            error={problem?.field === "confirm" ? t("auth.resetPassword.mismatch") : undefined}
            onChange={(event) => setConfirm(event.target.value)}
          />
        ) : null}
        {mode === "existing" ? (
          <p className="g-auth__hint">
            <Link to="/login">{t("invitation.forgotPassword")}</Link>
          </p>
        ) : null}
        {problem?.field === "form" ? (
          <p className="g-auth__message" data-tone="error" role="alert">
            {messageForError(t, problem.error)}
          </p>
        ) : null}
        <Button ref={submitRef} variant="primary" type="submit" disabled={submitting}>
          {submitting ? t("invitation.submitting") : t("invitation.submit", { organization: preview.organizationName })}
        </Button>
      </form>
    </>
  );
}
